import { action, observable, makeObservable } from 'mobx';
import moment from 'moment/min/moment-with-locales';
import _ from 'lodash';

//service
import agent from '../agent';

//stores
import userStore from './userStore';
import commonStore from './commonStore';

//utils
import { getMinutesFromTimeInput, minutesToTimeInput } from '../../utils/timeCalc';
import { getCompanyShiftDetails } from '../../utils/commonMethods';
import { TIMESHEET_CONSTANT } from '../../utils/constants';

const timelogInitial = {
    task_id: null,
    workplace: '',
    from: '08:00',
    to: '16:00',
    break: 0,
    total_hours_normal: '08:00',
    total_hours_overtime: '00:00',
    total_hours_overall: '08:00',
    for_date: new Date(),
    data: {},
    distance: sessionStorage.getItem('last_km_filled') || 0,
    signature: null,
    Attachments: [],
    company_shift_id: null
};

class TimelogStore {
    constructor() {
        makeObservable(this, {
            currentList: observable,
            currentEntity: observable,
            originalEntity: observable,
            applicableManualExtraPaymentTypesFor: observable,
            updatingErrors: observable,
            updating: observable,
            loading: observable,
            requestParams: observable,
            deleteSuccess: observable,
            lastListLoadTime: observable,
            allLocations: observable,
            resetLastListLoadTime: action,
            timelogShift: observable,
            tip_Filter: observable,
            appliedTipFilters: observable,
            filters: observable,
            appliedFilters: observable,
            currentMultiTaskTimeLogs: observable,
            overlapAbsence: observable,
            canEditCompanyShift: observable,
            scheduledShift: observable,
            isShiftLoading: observable,
            setFilter: action,
            onFilter: action,
            setTipFilter: action,
            onTipFilter: action,
            massPushNotification: action,
            singlePushNotification: action,
            loadList: action,
            forDateLoadList: action,
            forDateLoadIntList: action,
            changeEntity: action,
            calcTotalTime: action,
            calcOvertimes: action,
            load: action,
            saveAndOverrideCurrentEntity: action,
            save: action,
            remove: action,
            setStatus: action,
            setMedicalLeaveMassStatus: action,
            getPossibleDraftId: action,
            getCurrentDraftOrSkip: action,
            getCurrentDraftForLocked: action,
            deleteTip: action,
            updateTip: action,
            getTipList: action,
            finishByButton: action,
            reportInjury: action,
            startByButton: action,
            addTip: action,
            switchToManual: action,
            breakByButton: action,
            getAllLocations: action,
            updateLoaderState: action,
            validateTimelogWithIntegration: action,
            cancelPendingAbsenceOrVacation: action,
            updateTimesheetToManuallyManagedMode: action,
            validateTimelogAndReset: action,
            lastInteractedId: observable,
            setLastInteractedId: action,
            clearLastInteractedId: action,
        });
    }

    currentList = [];
    currentEntity = timelogInitial;
    originalEntity = timelogInitial;
    applicableManualExtraPaymentTypesFor = null;
    updatingErrors = null;
    updating = false;
    loading = false;
    requestParams = null;
    deleteSuccess = false;
    lastListLoadTime = null;
    lastInteractedId = null;
    allLocations = [];
    overlapAbsence = [];
    timelogShift = null;
    canEditCompanyShift = false;
    scheduledShift = null;
    isShiftLoading = false;
    tip_Filter = {
        status: '',
        name: '',
    };
    appliedTipFilters = {
        name: '',
    };
    filters = {
        status: '',
        name: '',
        showAnomalies: false,
    };
    appliedFilters = {
        status: '',
        name: '',
        showAnomalies: false,
    };

    currentMultiTaskTimeLogs = [];

    resetLastListLoadTime(value) {
        this.lastListLoadTime = value;
    };

    setLastInteractedId(id) {
        this.lastInteractedId = id ?? null;
    }

    clearLastInteractedId() {
        this.lastInteractedId = null;
    }

    setFilter(name, value, clearFilter) {
        const filters = Object.assign({}, this.filters);
        filters[name] = value;
        this.filters = filters;
        if (clearFilter) {
            this.filters = {}
        }
    }

    onFilter() {
        this.appliedFilters = Object.assign({}, this.filters);
    }

    setTipFilter(name, value) {
        const tip = Object.assign({}, this.tip_Filter);
        tip[name] = value;
        this.tip_Filter = tip;
    }

    onTipFilter() {
        this.appliedTipFilters = Object.assign({}, this.tip_Filter);
    }

    massPushNotification(ids, comment, action) {
        const users = this.currentList.rows.filter(row => ids.indexOf(`${row.id}`) >= 0).map(row => row.user_id);
        const uniqueUsers = [...(new Set(users))];
        return Promise.all(uniqueUsers.map(id => agent.Timelogs.sendMassStatusPushNotification(id, comment, action)));
    }

    singlePushNotification(id, comment, action, syncWithIntegration) {
        return agent.Timelogs.sendSingleStatusPushNotification(id, comment, action, syncWithIntegration);
    }

    updateLoaderState(status) {
        this.loading = status;
    }

    loadList(params) {
        return agent.Timelogs.list(params)
            .then(
                action((list) => {
                    list.rows && list.rows.forEach((item) => {
                        if (item.status === 'draft') item.isDisabled = true
                    })
                    commonStore.setIsUserLoggedIn(list.is_user_logged_in);
                    this.requestParams = params;
                    list.time = new Date();
                    this.lastListLoadTime = list.time;
                    this.currentList = list;
                    return list;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    forDateLoadList(user_id, for_date) {
        return agent.Timelogs.forDateList(user_id, for_date)
            .then(
                action((list) => {
                    this.currentMultiTaskTimeLogs = list.rows;
                    return list;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    forDateLoadIntList(user_id, for_date) {
        return agent.Timelogs.forDateIntList(user_id, for_date)
            .then(
                action((list) => {
                    this.currentMultiTaskTimeLogs = list.rows;
                    return list;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    changeEntity(name, value, kind, isAuto) {
        const entity = Object.assign({}, this.currentEntity);
        const nameArray = name.split('.');
        if (nameArray.length === 2) {
            const [firstProp, secondProp] = nameArray;
            entity[firstProp] = entity[firstProp] || {};
            entity[firstProp][secondProp] = value;
        } else if (nameArray.length === 3) {
            const [firstProp, secondProp, thirdProp] = nameArray;
            entity[firstProp] = entity[firstProp] || {};
            entity[firstProp][secondProp] = entity[firstProp][secondProp] || {};
            entity[firstProp][secondProp][thirdProp] = value;
        }
        else {
            entity[name] = value;
        }
        this.currentEntity = entity;
        if (name === 'from' || name === 'to' || name === 'break') {
            this.calcTotalTime(name);
        }
        if (kind === 'overtime') {
            this.calcOvertimes();
        }
        if (name === 'for_date' && entity.task_id && !isAuto && this.currentEntity.id) {
            let tempEntity = {
                id: this.currentEntity.id,
                for_date: moment(this.currentEntity.for_date).format('YYYY-MM-DD'),
                user_id: this.currentEntity.user_id
            }
            return agent.Timelogs.save(tempEntity, false).then(
                action((entity) => {
                    const timelog = this.prepareLoadedEntity(entity.timelog);
                    this.currentEntity = {
                        ...timelogInitial,
                        ...timelog,
                    };
                    this.originalEntity = {
                        ...timelogInitial,
                        ...timelog,
                    };
                    this.calcTotalTime();
                })
            ).catch(
                action((err) => {
                    throw err;
                })
            );
        }
    }

    changeAutoOvertimeProperties(name, currentEntity, config) {
        const overtimeAutomaticMode = config.client?.data?.basicRules?.overtimeAutomaticMode;
        const hasCompanyShifts = config?.client?.allow_company_shifts;
        if (!overtimeAutomaticMode || hasCompanyShifts) return;
        const entity = Object.assign({}, currentEntity);
        if (overtimeAutomaticMode && (name === 'from' || name === 'to')) {
            const overtimeThresoldMinutes = parseInt(config.client.data.basicRules.overtimeThresoldMinutes);
            const calc_period = config.client.data.basicRules.overtimeCalcRules.calc_period;
            const max_hours_in_mins = config.client.data.basicRules.overtimeCalcRules.max_hours * 60;
            const max_minutes = config.client.data.basicRules.overtimeCalcRules.max_minutes;
            const total_overtime_threshold_in_mins = max_hours_in_mins + max_minutes + overtimeThresoldMinutes;
            const overtime_limit_in_mins = max_hours_in_mins + max_minutes;

            const fromMinutes = getMinutesFromTimeInput(entity.from);
            const endMinutes = getMinutesFromTimeInput(entity.to);
            const breakMinutes = entity.break || 0;
            entity.breakMinutes = breakMinutes;
            let totalMinutes = endMinutes - fromMinutes;
            if (endMinutes < fromMinutes) {
                totalMinutes += 24 * 60;
            }
            if (totalMinutes > breakMinutes) {
                totalMinutes -= breakMinutes;
            }
            if (calc_period === 'day') {
                if (total_overtime_threshold_in_mins < totalMinutes) {
                    entity._total_hours_overtime = totalMinutes - (overtime_limit_in_mins);
                    entity._total_hours_normal = overtime_limit_in_mins;
                    this.currentEntity = entity;
                }
                else {
                    delete entity._total_hours_overtime
                    delete entity._total_hours_normal
                    this.currentEntity = entity;
                }
            }
        }
    }

    calcTotalTime(name = null) {
        const entity = Object.assign({}, this.currentEntity);
        const fromMinutes = getMinutesFromTimeInput(entity.from);
        const endMinutes = getMinutesFromTimeInput(entity.to);
        let breakMinutes = entity.break || 0; // Math.round(entity.break / 15) * 15;
        let totalMinutes = endMinutes - fromMinutes;
        if (endMinutes < fromMinutes) {
            // && fromMinutes > (12 * 60)
            // this is probably 12-h shift
            totalMinutes += 24 * 60;
        }
        // No breaks if less than 5.5 hours
        if (entity.status === 'draft' && totalMinutes <= TIMESHEET_CONSTANT.MINIMUM_HOURS_FOR_BREAK_IN_MIN && name !== 'break') {
            breakMinutes = 0;
        }
        entity.break = breakMinutes;
        entity.breakMinutes = breakMinutes;
        if (totalMinutes > breakMinutes) {
            totalMinutes -= breakMinutes;
        } else {
        }
        const totalResultPure = minutesToTimeInput(totalMinutes, 'full');
        entity.total_hours_overall = totalResultPure.positive ? totalResultPure.value : '00:00';
        totalMinutes -= getMinutesFromTimeInput(entity.total_hours_overtime);
        const totalResult = minutesToTimeInput(totalMinutes, 'full');
        entity.total_hours_normal = totalResult.positive ? totalResult.value : '00:00';
        this.currentEntity = entity;
    }

    calcOvertimes() {
        const entity = Object.assign({}, this.currentEntity);
        const totalOvertime = Object.values(entity.data.overtimes).reduce((overtime, over) => {
            if (over.valueInMins) {
                return overtime + over.valueInMins;
            } else {
                return overtime + getMinutesFromTimeInput(over.value);
            }
        }, 0);
        const totalOvertimeInTimeInput = minutesToTimeInput(totalOvertime, 'full');
        entity.total_hours_overtime = totalOvertimeInTimeInput.positive ? totalOvertimeInTimeInput.value : '00:00';
        this.currentEntity = entity;
        this.calcTotalTime();
    }

    prepareLoadedEntity(loadedData) {
        loadedData = loadedData || {};
        loadedData.data = loadedData.data || {};
        loadedData.distance = loadedData.data.distance || loadedData.distance;
        loadedData.for_date = new Date(loadedData.for_date);
        // Task/date/employee that applicableManualExtraPaymentTypes was worked out for; manual inputs hide once these change unsaved.
        this.applicableManualExtraPaymentTypesFor = { task_id: loadedData.task_id, for_date: moment(loadedData.for_date).format('YYYY-MM-DD'), user_id: loadedData.user_id };
        loadedData.from = minutesToTimeInput(loadedData.from, false);
        loadedData.to = minutesToTimeInput(loadedData.to, false);
        loadedData.total_hours_normal = minutesToTimeInput(loadedData.total_hours_normal, false);
        loadedData.total_hours_overtime = minutesToTimeInput(loadedData.total_hours_overtime, false);
        const breakMinutes = loadedData.break || 0; // Math.round(loadedData.break / 15) * 15;
        loadedData.break = breakMinutes;
        return loadedData;
    }

    validateTimelogifTimeBankPresent(timelog, config) {
        if (timelog.status !== 'active') return false;
        const { allowTimeBank = false } = config.client.data.basicRules.timeBankSettings || {};
        if (allowTimeBank && timelog.data?.timeBank && timelog.data?.timeBank?.length && timelog?.data?.timeBank.some(tb => tb.type === 'overtime')) {
            return true;
        }
        return false;
    }

    load(id) {
        this.loading = true;
        return agent.Timelogs.load(id)
            .then(
                action((response) => {
                    const timelog = this.prepareLoadedEntity(response.timelog);
                    this.timelogShift = response.shift;
                    this.overlapAbsence = response.overlapAbsence;
                    this.currentEntity = {
                        ...timelogInitial,
                        ...timelog,
                    };
                    this.originalEntity = {
                        ...timelogInitial,
                        ...timelog,
                    };
                    this.calcTotalTime();
                    this.loading = false;
                    return response;
                })
            )
            .catch(
                action((err) => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    saveAndOverrideCurrentEntity(values, isAdd, intermediate) {
        this.currentEntity = JSON.parse(JSON.stringify(values));
        return this.save(values, isAdd, intermediate);
    }

    save(values, isAdd, intermediate) {
        class ValidationError extends Error {
            constructor(message) {
                super(message);
                this.name = 'ValidationError';
            }
        }
        sessionStorage.setItem('last_km_filled', this.currentEntity.distance);
        this.currentEntity.intermediateSave = !!intermediate;

        if (!intermediate && !this.currentEntity.task_id) {
            this.updating = false;
            this.updatingErrors = { message: 'Task is required' };
            throw new ValidationError('Task is required');
        }

        if (intermediate && this.currentEntity.id) {
            this.currentEntity.for_date = moment(this.currentEntity.for_date).format('YYYY-MM-DD')
            return agent.Timelogs.save(this.currentEntity, false).then(
                action((tl) => {
                    this.changeEntity('distance_type', tl.timelog.distance_type);
                    this.changeEntity('data', tl.timelog.data);
                    this.changeEntity('applicableManualExtraPaymentTypes', tl.timelog.applicableManualExtraPaymentTypes);
                    this.applicableManualExtraPaymentTypesFor = { task_id: tl.timelog.task_id, for_date: moment(this.currentEntity.for_date).format('YYYY-MM-DD'), user_id: tl.timelog.user_id };
                    this.changeEntity('User', tl.timelog.User);
                    this.changeEntity('from', minutesToTimeInput(tl.timelog.from));
                    this.changeEntity('to', minutesToTimeInput(tl.timelog.to));
                    if (tl.timelog.company_shift_id && tl.timelog.company_shift_id !== this.currentEntity.company_shift_id) {
                        this.changeEntity('company_shift_id', tl.timelog.company_shift_id);
                    }
                    this.overlapAbsence = tl.overlapAbsence
                    return tl;
                })
            );
        }

        this.updating = true;
        const user = userStore.currentUser;
        const entity = _.cloneDeep(this.currentEntity);
        if (entity.status === 'draft' && !intermediate) entity.status = 'active';
        entity.for_date = moment(entity.for_date).format('YYYY-MM-DD');
        entity.autoAdjust = values?.autoAdjust
        if (!entity.id) isAdd = true;
        entity.is_web = true;
        return agent.Timelogs.save(entity, isAdd)
            .then(
                action((tl) => {
                    this.updating = false;
                    if (!tl.timelog) return tl;
                    this.changeEntity('distance_type', tl.timelog.distance_type);
                    if (tl.timelog.user_id === user.id) {
                        userStore.pullUser();
                    }
                    this.changeEntity('data', tl.timelog.data);
                    this.changeEntity('id', tl.timelog.id);
                    this.changeEntity('applicableManualExtraPaymentTypes', tl.timelog.applicableManualExtraPaymentTypes);
                    this.applicableManualExtraPaymentTypesFor = { task_id: tl.timelog.task_id, for_date: moment(this.currentEntity.for_date).format('YYYY-MM-DD'), user_id: tl.timelog.user_id };
                    this.changeEntity('User', tl.timelog.User);
                    this.changeEntity('from', minutesToTimeInput(tl.timelog.from));
                    this.changeEntity('to', minutesToTimeInput(tl.timelog.to));
                    this.overlapAbsence = tl.overlapAbsence
                    return tl;
                })
            )
            .catch((err) => {
                this.updating = false;
                if (err.response?.body)
                    return err.response.body
                throw err;
            });
    }

    quickTimeUpdate(values) {
        return agent.Timelogs.quickTimeUpdate(values)
            .then(
                action((tl) => {
                    return tl;
                })
            )
            .catch((err) => {
                if (err.response?.body)
                    return err.response.body
                throw err;
            });
    }

    async remove(id) {
        const response = await agent.Timelogs.remove(id);
        if (response?.userPrompts?.some(p => p.source === 'convertedOvertimeConflict')) {
            return response;
        }
        this.deleteSuccess = true;
        return 1;
    }

    async setStatus(id, entityName, status, _data = {}, syncWithIntegration, shiftCreationParams, addedExtraPayment) {
        const data = Object.assign({}, _data);
        data.status = status;
        data.syncWithIntegration = syncWithIntegration
        data.shiftCreationParams = shiftCreationParams;
        data.addedExtraPayment = addedExtraPayment;
        return agent.Timelogs.setStatus(entityName, id, data);
    }

    // action is 'approve_all' or 'reset_all', and ids the medical leaves the prompt listed, in its order
    async setMedicalLeaveMassStatus(id, massAction, ids, _data = {}, syncWithIntegration, shiftCreationParams, addedExtraPayment) {
        const data = Object.assign({}, _data);
        data.action = massAction;
        data.ids = ids;
        data.syncWithIntegration = syncWithIntegration;
        data.shiftCreationParams = shiftCreationParams;
        data.addedExtraPayment = addedExtraPayment;
        return agent.Timelogs.setMedicalLeaveMassStatus(id, data);
    }

    async resetApprovedWeeklyOvertimeTimesheets(approvedTimesheets = []) {
        const isMultiTaskTimesheetEnabled = commonStore.config?.client?.data?.basicRules?.allowMutiTaskTimesheets;
        const syncWithIntegration = commonStore.config?.integration_details?.product ? true : false;
        if (!isMultiTaskTimesheetEnabled) {
            for (const timesheet of approvedTimesheets) {
                await this.setStatus(timesheet.id, 'timelog', 'reset', {}, syncWithIntegration);
            }
            return;
        }
        // multi task clients can only change the status of a full day at once//
        const resetDays = [...new Map(approvedTimesheets.map(timesheet => [`${timesheet.user_id}_${timesheet.for_date}`, timesheet])).values()];
        for (const day of resetDays) {
            await this.multiTimelogStatus({
                user_id: day.user_id,
                for_date: day.for_date,
                status: 'reset',
                syncWithIntegration,
                notes: '',
                admin_note: ''
            });
        }
    }

    async getPossibleDraftId(autostart) {
        const r = await agent.Timelogs.getDraftId(autostart);
        if (autostart) {
            return r.timelog;
        }
        return r.timelog.id;
    }

    async getPossibleDraftTimelog() {
        const response = await agent.Timelogs.getDraftTimelog();

        const timelog = this.prepareLoadedEntity(response.timelog);
        this.timelogShift = response.shift;
        this.overlapAbsence = response.overlapAbsence;
        this.currentEntity = {
            ...timelogInitial,
            ...timelog,
            User: timelog.User,
            company_shift_id: timelog.User?.company_shift_id,
            allow_auto_flexitime: timelog.User?.allow_auto_flexitime
        };
        this.originalEntity = {
            ...timelogInitial,
            ...timelog,
            User: timelog.User,
            company_shift_id: timelog.User?.company_shift_id,
            allow_auto_flexitime: timelog.User?.allow_auto_flexitime
        };
        this.calcTotalTime();
        this.loading = false;
        return response;
    }

    async getCurrentDraftOrSkip(forceProject = '', user = '', onlyValid = false) {
        const r = await agent.Timelogs.getCurrentDraftOrSkip(forceProject, user, onlyValid);
        return r;
    }

    async getCurrentDraftForLocked(forceProject = '', user = '') {
        const r = await agent.Timelogs.getCurrentDraftForLocked(forceProject, user);
        return r;
    }

    async deleteTip(id) {
        await agent.Timelogs.deleteTip(id).then(
            action((res) => {
                return res;
            })
        ).catch(
            action((err) => {
                throw err;
            })
        );
    }

    async updateTip(id, data) {
        await agent.Timelogs.updateTip(id, data).then(
            action((res) => {
                return res;
            })
        ).catch(
            action((err) => {
                throw err;
            })
        );
    }

    getTipList(params) {
        this.loading = true;
        return agent.Timelogs.getTipList(params).then(
            action((res) => {
                this.loading = false;
                res.time = new Date();
                this.lastListLoadTime = res.time;
                return res;
            })
        ).catch(
            action((err) => {
                this.loading = false;
                throw err;
            })
        );
    }

    async finishByButton(timelog, data) {
        try {
            const response = await agent.Timelogs.finishByButton(timelog.id, data);
            if (response.timelog?.id) this.load(response.timelog.id);
            return response;
        }
        catch (err) {
            if (err.response?.body)
                return err.response.body
        }
    }

    async reportInjury(timelog, isInjury) {
        const response = await agent.Timelogs.reportInjury(timelog.id, isInjury);
        return response.timelog;
    }

    async startByButton(data) {
        try {
            const result = await agent.Timelogs.startByButton({ ...data, is_web: true });
            this.load(result.timelog.id);
            return result;
        }
        catch (e) {
            commonStore.addNotification(e, null, 'error');
            throw e;
        }
    }

    async addTip(data) {
        await agent.Timelogs.addTip(data).then(
            action((res) => {
                return res;
            })
        ).catch(
            action((err) => {
                throw err;
            })
        );
    }

    updateTimelogCurrentEntityUser = async (id) => {
        let user = userStore.currentUser
        if (user.id === id) return;
        user = await userStore.pullUserById(id);
        this.currentEntity.User = user;
        this.currentEntity.company_shift_id = user.company_shift_id;
    }

    setManualDefaults() {
        try {
            const isDisableAutoBreak = !this.currentEntity?.User?.auto_break;
            const clientAndConfigs = commonStore.config;
            if (this.scheduledShift) {
                this.currentEntity.from = minutesToTimeInput(this.scheduledShift.start_time, false);
                this.currentEntity.to = minutesToTimeInput(this.scheduledShift.end_time, false);
                this.currentEntity.data.shiftId = this.scheduledShift.id;
                const timeDetails = getCompanyShiftDetails(clientAndConfigs, this.scheduledShift.company_shift_id, minutesToTimeInput);
                if (this.scheduledShift.company_shift_id && this.currentEntity.company_shift_id !== this.scheduledShift.company_shift_id)
                    this.changeEntity('company_shift_id', this.scheduledShift.company_shift_id);
                if (isDisableAutoBreak)
                    this.currentEntity.break = 0;
                else
                    this.currentEntity.break = timeDetails.break;
            } else {
                const timeDetails = getCompanyShiftDetails(clientAndConfigs, this.currentEntity.company_shift_id, minutesToTimeInput);
                this.currentEntity.from = timeDetails.start;
                this.currentEntity.to = timeDetails.end;
                if (isDisableAutoBreak)
                    this.currentEntity.break = 0;
                else
                    this.currentEntity.break = timeDetails.break;
            }
            this.currentEntity.tracker_status = 'manual';
            this.currentEntity.status_note = '';
            this.calcTotalTime();
        } catch (err) {
            console.error(err);
        }
    }

    switchToManual() {
        if (this.currentEntity.gps_data) {
            this.currentEntity.gps_data.start = { manual: true };
            this.currentEntity.gps_data.end = { manual: true };
        }
        this.currentEntity.tracker_status = 'manual';
    }

    async breakByButton(timelog, action, reload) {
        const response = await agent.Timelogs.breakByButton(timelog, action);
        if (reload) {
            this.load(timelog);
        }
        return response.timelog;
    }

    async getAllLocations(params) {
        this.loading = true;
        const response = await agent.Timelogs.getAllLocations(params);
        this.allLocations = response;
        this.loading = false;
        return response;
    }

    getUserShiftDetails(userId, date, shiftId, skipShiftOverride = false) {
        this.isShiftLoading = true;
        return agent.Timelogs.getUserShiftDetails(userId, date, shiftId)
            .then(
                action((response) => {
                    this.scheduledShift = response.shift;
                    this.canEditCompanyShift = response.canEditShift;
                    this.isShiftLoading = false;
                    if (!skipShiftOverride) {
                        if (response.shift?.company_shift_id) {
                            if (this.currentEntity?.company_shift_id !== response.shift.company_shift_id) {
                                this.changeEntity('company_shift_id', response.shift.company_shift_id);
                            }
                        } else {
                            const primaryShiftId = this.currentEntity?.User?.company_shift_id || userStore.currentUser?.company_shift_id;
                            if (this.currentEntity?.company_shift_id !== primaryShiftId) {
                                this.changeEntity('company_shift_id', primaryShiftId || null);
                            }
                        }
                    }
                    return response.shift;
                })
            )
            .catch(
                action((err) => {
                    console.error(err);
                })
            );
    }

    validateTimelogWithIntegration(timelog) {
        return agent.Timelogs.validateTimelogWithIntegration(timelog).then(
            action((res) => {
                return res.message;
            })
        ).catch(
            action((err) => {
                throw err;
            })
        );
    }

    handleChangeExtraPayments = (extraPayment, value, isReturnNeeded) => {
        const { code } = extraPayment;
        const data = this.currentEntity.data ? Object.assign({}, this.currentEntity.data) : {};
        data.extraPayments = data.extraPayments || {};
        // "Fixed amount (quantity)" is entered and stored as the point itself (0-1.0);
        // other manual types use the hh:mm time input and are stored as minutes.
        const isPoints = extraPayment?.extraPaymentType?.value === 'manual_point_amount';
        data.extraPayments[code] = {
            ...extraPayment,
            value: isPoints ? (Number(value) || 0) : getMinutesFromTimeInput(value),
        };
        let extraPayments = JSON.parse(JSON.stringify(data.extraPayments));
        let newExtraPayment = extraPayments;

        for (let key in Object.keys(extraPayments)) {
            const consideredData = extraPayments[key];
            if (!consideredData?.value) {
                delete newExtraPayment[key];
            }
        }
        data.extraPayments = newExtraPayment;
        this.changeEntity('data', data);
        if (isReturnNeeded) {
            return data;
        }
    };

    cancelPendingAbsenceOrVacation(link) {
        return agent.Timelogs.cancelPendingAbsenceOrVacation(link)
            .then(
                action(res => {
                    return res;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    multiTimelogStatus(data) {
        return agent.Timelogs.multiTimelogStatus(data)
            .then(
                action(res => {
                    return res;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }


    saveOvertimeComment(timelogId, overtime_comment) {
        return agent.Timelogs.saveOvertimeComment(timelogId, overtime_comment)
            .then(
                action(response => {
                    if (this.currentEntity && this.currentEntity.id === timelogId)
                        this.currentEntity = { ...this.currentEntity, overtime_comment: response.timelog.overtime_comment };
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    updateTimesheetToManuallyManagedMode(timelogId, type) {
        this.loading = true;
        return agent.Timelogs.updateTimesheetToManuallyManagedMode(timelogId, type)
            .then(
                action(response => {
                    if (!response.timelog && response.userPrompts) {
                        this.loading = false;
                        return response;
                    }
                    const timelog = this.prepareLoadedEntity(response.timelog);
                    this.timelogShift = response.shift;
                    this.overlapAbsence = response.overlapAbsence;
                    this.currentEntity = { ...timelog };
                    this.originalEntity = { ...timelog };
                    this.calcTotalTime();
                    this.loading = false;
                    return response;
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    startTimelogAsMandatory(timelogId, data) {
        this.updateLoaderState(true);
        return agent.Timelogs.startTimelogAsMandatory(timelogId, data)
            .then(
                action(response => {
                    this.updateLoaderState(false);
                    return response;
                })
            )
            .catch(
                action(err => {
                    this.updateLoaderState(false);
                    throw err;
                })
            );
    }

    convertMandatoryTimesheetToNormal(timelogId, inDraft) {
        this.updateLoaderState(true);
        if (inDraft)
            return agent.Timelogs.convertMandatoryTimelogToNormal(timelogId)
                .then(
                    action(response => {
                        this.updateLoaderState(false);
                        return response;
                    })
                )
                .catch(
                    action(err => {
                        this.updateLoaderState(false);
                        throw err;
                    })
                );
        else
            return agent.Timelogs.removeMandatoryOvertimeFlexitime(timelogId)
                .then(
                    action(async response => {
                        const { timelog, message } = response;
                        try {
                            const data = await agent.Timelogs.save(timelog, false);
                            this.updateLoaderState(false);
                            this.currentEntity = { ...this.currentEntity, ...timelog }
                            return { timelog, message };
                        } catch (error) {
                            throw error
                        }
                    })
                )
                .catch(
                    action(err => {
                        this.updateLoaderState(false);
                        throw err;
                    })
                );
    }

    pastTimelogEntity() {
        this.currentEntity = timelogInitial;
    }

    validateTimelogAndReset(id) {
        return agent.Timelogs.validateTimelogAndReset(id)
            .then(
                action(res => {
                    return res;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    deleteTimebankAllByDate(data) {
        this.loading = true;
        return agent.TimeBank.deleteAllByDate(data)
            .then(
                action(response => {
                    this.loading = false;
                    return response;
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    deleteTimebankAndMakeTimelogsEditable(data, isDetetingFromCurrentTimelog = false) {
        this.loading = true;
        return agent.TimeBank.remove(data)
            .then(
                action(response => {
                    if (isDetetingFromCurrentTimelog) {
                        const timelog = this.prepareLoadedEntity(response.timelog);
                        this.currentEntity = { ...timelog };
                        this.originalEntity = { ...timelog };
                        this.calcTotalTime();
                        this.loading = false;
                        return response;
                    }
                    else {
                        this.loading = false;
                        return this.currentEntity;

                    }
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    getTimelogConflicts(data) {
        return agent.Timelogs.getTimelogConflicts(data)
            .then(
                action(res => {
                    return res;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    changeObservable(name, value) {
        this[name] = value;
    }
}

const _TimelogStore = new TimelogStore();
export default _TimelogStore;